import Button from '@/components/UI/Button';
import FormField from '@/components/UI/FormFields/Latest/FormField';
import Header from '@/components/UI/Header';
import { _isNil, _notNil } from '@/littledash';
import type { AgentSessionPhase, AgentStreamEventV1 } from '@/model/Agent.model';
import { AgentSessionService } from '@/support/AgentSessionService';
import { ResponseType } from '@/support/ApiService';
import { useAgentSession } from '@/support/Hooks/agent/useAgentSession';
import { useState, type ChangeEvent, type FC } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form@latest';
import styles from './InVivoStudyProtocolParser.module.scss';

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const DOCX_ACCEPT = `.docx,${DOCX_MIME}`;

interface ProtocolFormValues {
  file: File | null;
}

const isDocx = (file: File): boolean => file.name.toLowerCase().endsWith('.docx') || file.type === DOCX_MIME;

const uploadProtocolToS3 = async (file: File, signal?: AbortSignal): Promise<string> => {
  const presigned = await AgentSessionService.presignStudyProtocolUpload({ file_name: file.name }, signal);
  if (presigned.type !== ResponseType.Success) {
    throw new Error('Could not mint a presigned upload URL.');
  }
  const { url } = presigned.body.data;
  const putResponse = await fetch(url, {
    method: 'PUT',
    body: file,
    signal,
  });
  if (!putResponse.ok) {
    throw new Error(`S3 upload failed (${putResponse.status})`);
  }
  return url;
};

/**
 * Admin-console screen that exercises the Study Protocol Parser agent
 * end-to-end: takes a URL or S3 key for an already-uploaded protocol
 * document, kicks off a session, and renders the streaming progress.
 */
export const InVivoStudyProtocolParser: FC = () => {
  const formMethods = useForm<ProtocolFormValues>({
    mode: 'onSubmit',
    defaultValues: { file: null },
  });

  const { state, createSession, cancel, reset } = useAgentSession();
  const { phase, streamEvents, error } = state;

  const [fileError, setFileError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const file = formMethods.watch('file');

  const isBusy = isUploading || phase === 'creating' || phase === 'streaming';
  const submitDisabled = isBusy || _isNil(file);

  const handleSubmit = async (values: ProtocolFormValues) => {
    if (_isNil(values.file)) {
      return;
    }
    setFileError(null);
    setIsUploading(true);
    let uploadedUrl: string;
    try {
      uploadedUrl = await uploadProtocolToS3(values.file);
    } catch (uploadError) {
      setFileError(uploadError instanceof Error ? uploadError.message : 'Failed to upload protocol document.');
      return;
    } finally {
      setIsUploading(false);
    }
    await createSession((signal) => AgentSessionService.createStudyProtocolSession({ url: uploadedUrl }, signal));
  };

  const handleReset = () => {
    reset();
    formMethods.reset();
    setFileError(null);
  };

  return (
    <div
      className="pb3"
      data-test-component="InVivoStudyProtocolParser"
      data-test-element="container"
      data-testid="invivo-study-protocol-parser"
    >
      <Header
        mainHeaderText="InVivo Study Protocol Parser"
        subHeaderText="Parse an uploaded protocol document into a draft study"
      />
      <div className={`${styles.layout} pt4`}>
        <div className="ui-card pa4 flex flex-column w-50" data-test-element="form">
          <FormProvider {...formMethods}>
            <form onSubmit={formMethods.handleSubmit(handleSubmit)}>
              <Controller
                control={formMethods.control}
                name="file"
                rules={{ required: 'Protocol document is required' }}
                render={({ field: { onChange, name, ref } }) => (
                  <FormField
                    name="file"
                    label="Protocol document (.docx)"
                    hint="Upload a Word .docx protocol. PDF support is coming soon."
                    required
                    testId="invivo-study-protocol-parser__file"
                  >
                    <input
                      type="file"
                      name={name}
                      ref={ref}
                      accept={DOCX_ACCEPT}
                      disabled={isBusy}
                      aria-label="Protocol document"
                      data-testid="invivo-study-protocol-parser__file-input"
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        const picked = event.target.files?.[0] ?? null;
                        if (_notNil(picked) && !isDocx(picked)) {
                          setFileError('Only .docx files are supported today.');
                          onChange(null);
                          event.target.value = '';
                          return;
                        }
                        setFileError(null);
                        onChange(picked);
                      }}
                    />
                    {_notNil(fileError) && (
                      <div className="red mt2" data-testid="invivo-study-protocol-parser__file-error">
                        {fileError}
                      </div>
                    )}
                  </FormField>
                )}
              />
              <div className={`${styles.actions} mt4`} data-test-element="actions">
                <Button
                  submit
                  primary
                  disabled={submitDisabled}
                  loading={isUploading || phase === 'creating'}
                  loadingText={isUploading ? 'Uploading' : 'Starting'}
                  testId="invivo-study-protocol-parser__submit"
                >
                  Parse protocol
                </Button>
                {phase === 'streaming' && (
                  <Button outline onClick={cancel} testId="invivo-study-protocol-parser__cancel">
                    Cancel
                  </Button>
                )}
                {(phase === 'completed' || phase === 'failed' || phase === 'cancelled') && (
                  <Button plain onClick={handleReset} testId="invivo-study-protocol-parser__reset">
                    New document
                  </Button>
                )}
              </div>
            </form>
          </FormProvider>
        </div>
        <div className="w-50" data-test-element="progress">
          <ProtocolProgress phase={phase} streamEvents={streamEvents} error={error} />
        </div>
      </div>
    </div>
  );
};

interface ProtocolProgressProps {
  phase: AgentSessionPhase;
  streamEvents: Array<AgentStreamEventV1>;
  error: string | null;
}

const placeholder: Record<AgentSessionPhase, string> = {
  idle: 'Upload a protocol document to begin parsing.',
  creating: 'Starting parser session…',
  streaming: 'Parsing protocol…',
  completed: '',
  failed: '',
  cancelled: 'Cancelled.',
};

const humanizePhase = (phase: string): string =>
  phase.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

interface FeedItem {
  key: string;
  label: string;
  done: boolean;
}

const buildFeedItems = (events: Array<AgentStreamEventV1>): Array<FeedItem> => {
  const phaseStartIndex = new Map<string, number>();
  const items: Array<FeedItem> = [];

  events.forEach((event, index) => {
    if (event.event_type === 'execution_started') {
      items.push({ key: `start-${index}`, label: 'Execution started', done: true });
      return;
    }
    if (event.event_type === 'thinking') {
      const payload = (event.payload as Record<string, unknown> | null) ?? null;
      const phase = typeof payload?.phase === 'string' ? payload.phase : null;
      const status = typeof payload?.status === 'string' ? payload.status : null;
      if (_isNil(phase)) {
        return;
      }
      if (status === 'start') {
        phaseStartIndex.set(phase, items.length);
        items.push({ key: `phase-${phase}-${index}`, label: humanizePhase(phase), done: false });
        return;
      }
      if (status === 'done') {
        const startIdx = phaseStartIndex.get(phase);
        if (_notNil(startIdx)) {
          items[startIdx] = { ...items[startIdx], done: true };
        } else {
          items.push({ key: `phase-${phase}-${index}`, label: humanizePhase(phase), done: true });
        }
        return;
      }
      return;
    }
    if (event.event_type === 'tool_call_start') {
      const step = _notNil(event.step_number) ? `Step ${event.step_number} · ` : '';
      items.push({
        key: `tool-start-${index}`,
        label: `${step}${event.tool_name ?? 'tool'} (running)`,
        done: false,
      });
      return;
    }
    if (event.event_type === 'tool_call_end') {
      const step = _notNil(event.step_number) ? `Step ${event.step_number} · ` : '';
      items.push({ key: `tool-end-${index}`, label: `${step}${event.tool_name ?? 'tool'}`, done: true });
    }
  });

  return items;
};

/**
 * Right-hand panel showing live progress for the parser session: the
 * harness emits coarse-grained phase events (extract_simple_study_data,
 * extract_animals, …) as `thinking` SSE events with payload.phase/status
 * — render each as a step row that flips to "done" when the matching
 * `done` event arrives.
 */
const ProtocolProgress: FC<ProtocolProgressProps> = ({ phase, streamEvents, error }) => {
  const items = buildFeedItems(streamEvents);
  return (
    <div
      className="ui-card pa4 flex flex-column"
      data-test-component="ProtocolProgress"
      data-test-element="container"
      data-testid="invivo-study-protocol-parser__progress"
    >
      <h2 className="f4 fw5 ma0 mb3" data-testid="invivo-study-protocol-parser__progress-title">
        Parser Progress
      </h2>
      {phase === 'failed' && (
        <div className="red mb3" data-testid="invivo-study-protocol-parser__error">
          {error ?? 'Parser failed.'}
        </div>
      )}
      {items.length === 0 ? (
        <div className="dark-gray" data-testid="invivo-study-protocol-parser__placeholder">
          {placeholder[phase]}
        </div>
      ) : (
        <ol className={styles.feed} data-testid="invivo-study-protocol-parser__feed">
          {items.map((item) => (
            <li key={item.key} className={`${styles.feedItem} ${item.done ? styles.feedItemDone : ''}`}>
              {item.done ? '✓ ' : '… '}
              {item.label}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};
